import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Background,
  BackgroundVariant,
  Controls,
  type Edge,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Button, useToast } from "@design";
import { Plus, Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FlowConnectorConfig, FlowResponse, FlowStepConfig } from "@/api";
import { getFlowQueryKey, listAgentsOptions, updateFlowMutation } from "@/api";
import { Text } from "@/components/Text";
import {
  buildParentMapFromConnectors,
  buildTreeEdges,
  collectSubtree,
  computeTreeLayout,
  findLastLeaf,
  generateNodeId,
  NODE_X,
  type StepNode,
  type StepNodeData,
  serializeSteps,
} from "./flowEditorModel";
import { AgentPicker, StepConfig } from "./StepConfigPanel";
import { StepNodeView } from "./StepNodeView";

/**
 * The visual step editor (React Flow v12). Ported from the old UI's
 * FlowBuilder, adapted to design tokens. The tree model + layout live in
 * flowEditorModel.ts (pure, unit-tested); this is the canvas; the node and
 * the step config panel are in StepNodeView/StepConfigPanel. Saves the whole
 * steps/connectors array via the flow PUT.
 */
export function FlowStepsEditor({ flow }: Readonly<{ flow: FlowResponse }>) {
  return (
    <ReactFlowProvider>
      <FlowStepsEditorInner flow={flow} />
    </ReactFlowProvider>
  );
}

const nodeTypes = { step: StepNodeView };

function FlowStepsEditorInner({ flow }: Readonly<{ flow: FlowResponse }>) {
  const { t } = useTranslation("workflows");
  const toast = useToast();
  const { fitView } = useReactFlow();
  const agents = useQuery(listAgentsOptions({ query: { page_size: 100 } }));
  const [nodes, setNodes, onNodesChange] = useNodesState<StepNode>([]);
  const [edges, setEdges] = useEdgesState<Edge>([]);
  const [parentMap, setParentMap] = useState<Map<string, string | null>>(new Map());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pickingAgent, setPickingAgent] = useState(false);
  const initialized = useRef(false);

  const newStepNode = (id: string, index: number, h: typeof handlers.current): StepNode => ({
    id,
    type: "step",
    position: { x: NODE_X, y: 0 },
    data: { requiresReview: false, config: {}, label: `${t("editor.stepConfig")} ${index}`, ...h },
  });

  const addStep = (parentId: string | null) => {
    const newId = generateNodeId();
    setParentMap((prev) => new Map(prev).set(newId, parentId));
    setNodes((nds) => [...nds, newStepNode(newId, nds.length + 1, handlers.current)]);
  };

  const handlers = useRef({
    onDelete: (nodeId: string) => {
      setParentMap((prev) => {
        const next = new Map(prev);
        for (const id of collectSubtree(nodeId, prev)) next.delete(id);
        return next;
      });
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setSelectedId((prev) => (prev === nodeId ? null : prev));
    },
    onAddChild: (nodeId: string) => addStep(nodeId),
    onInsertBefore: (nodeId: string) => {
      const newId = generateNodeId();
      setParentMap((prev) => {
        const next = new Map(prev);
        next.set(newId, prev.get(nodeId) ?? null);
        next.set(nodeId, newId);
        return next;
      });
      setNodes((nds) => [...nds, newStepNode(newId, nds.length + 1, handlers.current)]);
    },
  });

  // Initialise from the flow's saved steps/connectors.
  useEffect(() => {
    if (initialized.current || !flow.steps) return;
    initialized.current = true;
    const initNodes: StepNode[] = [];
    const dbIdToNodeId = new Map<number, string>();
    flow.steps.forEach((step, i) => {
      const id = generateNodeId();
      dbIdToNodeId.set(step.id, id);
      initNodes.push({
        id,
        type: "step",
        position: { x: NODE_X, y: 0 },
        data: {
          agentUuid: step.agentUuid ?? undefined,
          agentName: step.agentName ?? undefined,
          requiresReview: step.requiresReview,
          config: step.config,
          label: (step.config?.label as string) || step.agentName || `${t("editor.stepConfig")} ${i + 1}`,
          ...handlers.current,
        },
      });
    });
    setNodes(initNodes);
    setParentMap(
      buildParentMapFromConnectors(
        initNodes,
        (flow.connectors ?? []).map((c) => ({ fromStepId: c.fromStepId ?? undefined, toStepId: c.toStepId })),
        dbIdToNodeId,
      ),
    );
  }, [flow, setNodes, t]);

  // Relayout + rebuild edges whenever the tree's STRUCTURE changes. Depends on
  // parentMap and node count only — it calls setNodes to apply positions, so
  // depending on `nodes` itself would loop. Positions/handlers are reapplied
  // from the latest nodes inside the updater.
  const nodeCount = nodes.length;
  useEffect(() => {
    if (nodeCount === 0) {
      setEdges([]);
      return;
    }
    setNodes((current) => {
      const positions = computeTreeLayout(current, parentMap);
      const withLayout = current.map((n) => ({
        ...n,
        position: positions.get(n.id) ?? n.position,
        data: { ...n.data, ...handlers.current },
      }));
      setEdges(buildTreeEdges(withLayout, parentMap));
      return withLayout;
    });
    setTimeout(() => fitView({ padding: 0.2, duration: 200 }), 50);
  }, [parentMap, nodeCount, setEdges, setNodes, fitView]);

  const selected = nodes.find((n) => n.id === selectedId) ?? null;
  const updateSelected = (patch: Partial<StepNodeData>) => {
    if (!selected) return;
    setNodes((nds) => nds.map((n) => (n.id === selected.id ? { ...n, data: { ...n.data, ...patch } } : n)));
  };

  const queryClient = useQueryClient();
  const saveMutation = useMutation(updateFlowMutation());
  const handleSave = async () => {
    const { steps, connectors } = serializeSteps(nodes, parentMap);
    try {
      await saveMutation.mutateAsync({
        path: { flow_uuid: flow.uuid },
        body: { steps: steps as FlowStepConfig[], connectors: connectors as unknown as FlowConnectorConfig[] },
        throwOnError: true,
      });
      await queryClient.invalidateQueries({ queryKey: getFlowQueryKey({ path: { flow_uuid: flow.uuid } }) });
      toast.success(t("editor.saved"));
    } catch {
      toast.error(t("editor.saveFailed"));
    }
  };

  return (
    <div className="flex h-[600px] overflow-hidden rounded-lg border border-border-input bg-surface-base">
      <div className="relative flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={(changes) => onNodesChange(changes.filter((c) => c.type !== "position"))}
          onNodeClick={(_, node) => {
            setSelectedId(node.id);
            setPickingAgent(false);
          }}
          nodeTypes={nodeTypes}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          proOptions={{ hideAttribution: true }}
          colorMode="system"
        >
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          <Controls />
          <Panel position="top-left">
            <Button
              variant="primary"
              leadingIcon={<Plus size={16} />}
              onClick={() => addStep(findLastLeaf(nodes, parentMap))}
            >
              {t("editor.addStep")}
            </Button>
          </Panel>
          <Panel position="top-right">
            <Button
              variant="primary"
              leadingIcon={<Save size={16} />}
              onClick={handleSave}
              disabled={nodes.length === 0}
            >
              {t("editor.saveFlow")}
            </Button>
          </Panel>
          {nodes.length > 0 && (
            <Panel position="bottom-left">
              <div className="flex flex-col gap-1 rounded-lg border border-border-input bg-surface-primary/90 px-3 py-2">
                <Text variant="caption" tone="tertiary">
                  {t("editor.hintFanOut")}
                </Text>
                <Text variant="caption" tone="tertiary">
                  {t("editor.hintParallel")}
                </Text>
              </div>
            </Panel>
          )}
        </ReactFlow>
        {nodes.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <Text variant="body-sm" tone="tertiary">
              {t("editor.empty")}
            </Text>
          </div>
        )}
      </div>

      {selected && (
        <div className="w-80 overflow-y-auto border-l border-border-subtle bg-surface-primary p-4">
          {pickingAgent ? (
            <AgentPicker
              agents={agents.data?.data ?? []}
              loading={agents.isPending}
              onAssign={(a) => {
                updateSelected({ agentUuid: a.uuid, agentName: a.name, label: selected.data.label || a.name });
                setPickingAgent(false);
              }}
              onClose={() => setPickingAgent(false)}
            />
          ) : (
            <StepConfig
              node={selected}
              parentMap={parentMap}
              nodes={nodes}
              onChange={updateSelected}
              onChangeParent={(parentId) => setParentMap((prev) => new Map(prev).set(selected.id, parentId))}
              onPickAgent={() => setPickingAgent(true)}
              onClearAgent={() => updateSelected({ agentUuid: undefined, agentName: undefined })}
              onDelete={() => handlers.current.onDelete(selected.id)}
              onClose={() => setSelectedId(null)}
            />
          )}
        </div>
      )}
    </div>
  );
}
